import { useQuery } from '@tanstack/react-query';
import { AdvancedFilterPanel } from '@/components/common/AdvancedFilter';
import { activeExamBoardsQueryOptions } from '@/features/examBoards/api/examBoards.api';
import { activeInstituteTypesQueryOptions } from '@/features/instituteTypes/api/instituteTypes.api';
import { useGeoCascade } from '@/hooks/useGeoCascade';
import { getFilterPanelProps, type UrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import {
  getActiveStatusFilterField,
  getExamBoardFilterField,
  getGeoFilterFields,
  getInstituteTypeFilterField,
  withRequiredFilterFields,
} from '@/utils/filterFields';
import { FILTER_KEYS, parseNumberFilter } from '@/utils/filters';

const STATUS_FILTER_KEY = 'status';
const REQUIRED_FILTER_KEYS = [
  'instituteTypeId',
  FILTER_KEYS.divisionId,
  FILTER_KEYS.districtId,
] as const;

interface InstituteFilterPanelProps {
  filters: UrlSyncedFilters;
}

export function InstituteFilterPanel({ filters }: InstituteFilterPanelProps) {
  const { filterValues } = filters;

  const { data: instituteTypes } = useQuery(activeInstituteTypesQueryOptions());
  const { data: examBoards } = useQuery(activeExamBoardsQueryOptions());
  const geoOptions = useGeoCascade({
    divisionId: parseNumberFilter(filterValues[FILTER_KEYS.divisionId]),
    districtId: parseNumberFilter(filterValues[FILTER_KEYS.districtId]),
  });

  return (
    <AdvancedFilterPanel
      fields={withRequiredFilterFields(
        [
          { ...getInstituteTypeFilterField(instituteTypes), placeholder: 'Pick an institute type' },
          ...getGeoFilterFields(filterValues, geoOptions),
          getExamBoardFilterField(examBoards),
          { key: 'eiinNumber', label: 'EIIN Number', type: 'text', placeholder: '123456' },
          getActiveStatusFilterField(STATUS_FILTER_KEY),
        ],
        REQUIRED_FILTER_KEYS,
      )}
      {...getFilterPanelProps(filters)}
    />
  );
}
